/* eslint-disable no-console */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Form } from '@unform/web';
import { ApexOptions } from 'apexcharts';
import { FormHandles } from '@unform/core';
import { Container } from './styles';
import TransferCard from '~/components/TransferCard';
import EntriesCard from '~/components/EntriesCard';
import LotsCard from '~/components/LotsCard';
import ScoreCard from '~/components/ScoreCard';

import Select, { IOption } from '~/components/Select';
import BtnNewEvent from '~/components/BtnNewEvent';
import { useResize } from '~/hooks/Resize';
import api from '~/services/api';
import { formatPrice } from '~/utils/format';
import Loading from '~/components/Loading';

interface ICashFlowResponse {
  month: number;
  year: number;
  accumulated: number;
  received: number;
  drawn: number;
  receivables: number;
  balance: number;
  cancelled: number;
}

const Home: React.FC = () => {
  const { width } = useResize();
  const formRefEvent = useRef<FormHandles>(null);
  const [showValues, setShowValues] = useState(true);
  const [donutChart, setDonutChart] = useState(300);
  const [lineChart, setLineChart] = useState(450);
  const [available, setAvailable] = useState(0);
  const [total, setTotal] = useState(0);
  const [drawn, setDrawn] = useState(0);
  const [retained, setRetained] = useState(0);
  const [cancelled, setCancelled] = useState(0);
  const [nameUser, setNameUser] = useState('');
  const [nameOrganizer, setNameOrganizer] = useState('');
  const [eventsModal, setEventsModal] = useState<IOption[]>([]);
  const [eventName, setEventName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [, setOrganizerId] = useState<number | null>(null);
  const [eventsList, setEventsList] = useState<any[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .get<ICashFlowResponse>('cash-flows/organizer/graph')
      .then((response) => {
        const cashFlow = response.data;
        if (Object.keys(cashFlow).length > 0) {
          setAvailable(cashFlow.balance);
          setTotal(cashFlow.accumulated);
          setDrawn(cashFlow.drawn);
          setRetained(cashFlow.receivables);
          setCancelled(cashFlow.cancelled);
        }
      });
  }, []);

  useEffect(() => {
    const user = localStorage.getItem('@Koro:user');
    if (user) {
      const parsed = JSON.parse(user);
      setNameUser(parsed.name);
      if (parsed.organizersUsers?.length > 0) {
        setNameOrganizer(
          `(${parsed.organizersUsers[0].organizer_id}) ${parsed.organizersUsers[0].organizer.name}`
        );
      }
    }
  }, []);

  const seriesTransfer = useMemo(
    () =>
      total > 0
        ? [
            parseFloat(available.toString()),
            parseFloat(drawn.toString()),
            parseFloat(retained.toString()),
            parseFloat(cancelled.toString()),
          ]
        : [100],
    [available, drawn, retained, total, cancelled]
  );

  const optionsTransfer = useMemo(() => {
    const data: ApexOptions = {
      colors:
        total > 0 ? ['#002f6c', '#ffb580', '#ff842f', '#b30000'] : ['#d3d3d3'],
      chart: {
        type: 'donut',
      },

      plotOptions: {
        pie: {
          expandOnClick: false,

          startAngle: 0,
          endAngle: 360,
          donut: {
            size: '65%',
            labels: {
              show: false,
            },
          },
        },
      },

      legend: {
        customLegendItems: ['item1', 'item2', 'item3'],
        show: false,
      },
      dataLabels: {
        enabled: false,
      },

      stroke: {
        show: true,
        width: total > 0 ? 8 : 0,
        lineCap: 'round',
      },

      tooltip: {
        y: {
          formatter: (value) => {
            return formatPrice(total > 0 ? value : 0);
          },
        },
      },

      labels: ['Disponível para saque', 'Sacado', 'Retido', 'Cancelados'],
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 200,
              height: 200,
            },
            legend: {
              position: 'bottom',
            },
          },
        },
      ],
    };
    return data;
  }, [total]);

  useEffect(() => {
    if (width > 1200) {
      setDonutChart(240);
      setLineChart(340);
    }
    if (width > 1400) {
      setDonutChart(270);
      setLineChart(390);
    }
    if (width > 1600) {
      setDonutChart(300);
    }
    if (width > 1700) {
      setLineChart(440);
    }
  }, [width]);

  const loadEventData = useCallback(async (event: any) => {
    if (event) {
      try {
        setLoading(true);
        const response = await api.get(`events/${event.slug}`);

        console.log(response.data);
      } catch (error) {
        // console.log(error);
      } finally {
        setLoading(false);
      }
    }
  }, []);

  const handleEventValues = useCallback(async (event: IOption) => {
    setLoading(true);

    if (event.value === 'Todos') {
      const response = await api
        .get<ICashFlowResponse[]>('cash-flows/organizer')
        .finally(() => {
          setLoading(false);
        });

      const cashFlows = response.data.reverse();
      if (cashFlows.length > 0) {
        setAvailable(cashFlows[0].balance);
        setTotal(cashFlows[0].accumulated);
        setDrawn(cashFlows[0].drawn);
        setRetained(cashFlows[0].receivables);
        setCancelled(cashFlows[0].cancelled);
      }
    } else {
      const response = await api
        .get(`cash-flow-events/events/${event.id}`)
        .finally(() => {
          setLoading(false);
        });

      const cashFlows = response.data.reverse();
      if (cashFlows.length > 0) {
        setAvailable(cashFlows[0].balance);
        setTotal(cashFlows[0].accumulated);
        setDrawn(cashFlows[0].drawn);
        setRetained(cashFlows[0].receivables);
        setCancelled(cashFlows[0].cancelled);
      }
    }
  }, []);

  const handleLoadEventsModal = useCallback(async (search = '') => {
    try {
      const response = await api.get('events/organizer/dropdown', {
        params: {
          search: search.trim(),
          status_ids: [3, 4, 5, 6, 7, 8, 9],
        },
      });
      const data = response.data
        .map((event: any) => ({
          id: event?.id,
          value: `${event?.name} (${event?.id})`,
          selected: false,
        }))
        .sort((a: IOption, b: IOption) => a.value.localeCompare(b.value));
      setOrganizerId(response.data[0]?.organizer_id);
      setEventsList(response.data);
      setEventsModal([
        {
          id: '',
          value: 'Todos',
          selected: true,
        } as IOption,
        ...data,
      ]);
    } catch (error) {
      console.log(error);
    }
  }, []);

  const handleChangeTextEvent = useCallback((text) => {
    const value = text === 'Todos' ? '' : text;
    setEventName(value);
    setSearchTerm(value);
  }, []);

  const handleSelectEvent = useCallback(
    (event: IOption) => {
      const eventFound = eventsList.find((e) => e.id === Number(event.id));
      setSelectedEvent(eventFound);
      setEventName(event.value === 'Todos' ? '' : event.value);
      setSearchTerm('');
      setSelectedEventId(event.value === 'Todos' ? null : Number(event.id));
      handleEventValues(event);
      // loadEventData(eventFound);
    },
    [eventsList, handleEventValues]
  );

  const handleSubmitEvent = useCallback(async () => {
    loadEventData(selectedEvent);
  }, [selectedEvent, loadEventData]);

  useEffect(() => {
    handleLoadEventsModal(debouncedSearch);
  }, [debouncedSearch, handleLoadEventsModal]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  return (
    <Container>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12 px-0">
            <div className="zoom bg-white d-flex justify-content-between align-items-center p-4">
              <h1 className="mb-0">
                {nameOrganizer ?? 'Organizador'} - {nameUser}
              </h1>
              <Form
                ref={formRefEvent}
                onSubmit={handleSubmitEvent}
                className="w-50"
              >
                <div className="d-flex justify-content-center align-items-center">
                  <h2 className="h5 mb-0 text-end w-50 me-3">
                    Filtre por evento
                  </h2>
                  <Select
                    name="event"
                    options={eventsModal}
                    placeholder="Selecione o evento"
                    onChangeText={handleChangeTextEvent}
                    value={eventName}
                    onChange={(e: any) => handleSelectEvent(e)}
                    className="bg-white rounded"
                  />
                </div>
              </Form>
              <BtnNewEvent />
            </div>
          </div>
          <div className="col-lg-8">
            <div className="row">
              <TransferCard
                showValues={showValues}
                setShowValues={setShowValues}
                total={total}
                available={available}
                drawn={drawn}
                retained={retained}
                cancelled={cancelled}
                optionsTransfer={optionsTransfer}
                seriesTransfer={seriesTransfer}
                donutChart={donutChart}
              />
              <EntriesCard
                lineChart={lineChart}
                selectedEventId={selectedEventId}
              />
            </div>
          </div>
          <div className="col-lg-4 mt-3 ps-3 pe-0">
            <LotsCard selectedEventId={selectedEventId} />
            <ScoreCard />
          </div>
        </div>
      </div>
      <Loading active={loading} text="Carregando..." />
    </Container>
  );
};

export default Home;

//
